import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportRemarkingAuditEvent, fetchRemarkingAuditEvent} from 'api/remarkingAudit';
import {ReactComponent as DownloadIcon} from 'assets/svg/download.svg';
import {AuditGeneralInformation} from 'components/common/AuditLog/GeneralInformation';
import {
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    EventBadge,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
} from 'components/common/AuditLog/styled';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Spinner} from 'components/ui-kit/Spinner';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {RemarkingAuditEntry, RemarkingAuditEventType} from 'types/remarking-audit';

const eventFields: Record<RemarkingAuditEventType, Array<keyof RemarkingAuditEntry>> = {
    REMARKING_DOCUMENT_CREATED: ['documentId', 'oldMarkingCode', 'remarkingReason', 'comment'],
    REMARKING_DOCUMENT_SUBMITTED: ['documentId', 'oldMarkingCode', 'oldCodeStatusAtSubmission'],
    REMARKING_DOCUMENT_PROCESSING: ['documentId', 'oldMarkingCode'],
    REMARKING_DOCUMENT_COMPLETED: [
        'documentId',
        'oldMarkingCode',
        'oldCodeStatusBeforeRemarking',
        'oldCodeStatusAfterRemarking',
        'oldCodeDisposalReason',
        'newMarkingCode',
        'newCodeStatus',
        'replacedByMarkingCodeId',
        'replacesMarkingCodeId',
        'remarkingReason',
    ],
    REMARKING_DOCUMENT_REJECTED: ['documentId', 'oldMarkingCode', 'rejectionReason', 'errorCode'],
};

const statusFields: Array<keyof RemarkingAuditEntry> = [
    'oldCodeStatusAtSubmission',
    'oldCodeStatusBeforeRemarking',
    'oldCodeStatusAfterRemarking',
    'newCodeStatus',
];

export const RemarkingAuditDetailPage = () => {
    const {id} = useParams<{id: string}>();
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const [entry, setEntry] = useState<RemarkingAuditEntry | null>(null);
    const [pending, setPending] = useState(true);
    const [exportPending, setExportPending] = useState(false);
    useEffect(() => {
        let active = true;
        setEntry(null);
        setPending(true);
        fetchRemarkingAuditEvent(id)
            .then((value) => {
                if (active) setEntry(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            })
            .finally(() => {
                if (active) setPending(false);
            });
        return () => {
            active = false;
        };
    }, [id]);
    const exportPdf = async () => {
        if (exportPending) return;
        setExportPending(true);
        try {
            saveAs(await exportRemarkingAuditEvent(id), `remarking-audit-${id}.pdf`);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setExportPending(false);
        }
    };
    const renderValue = (key: keyof RemarkingAuditEntry): React.ReactNode => {
        const value = entry?.[key];
        if (value == null || value === '') return '—';
        if (statusFields.includes(key)) {
            const status = String(value);
            const color = Object.prototype.hasOwnProperty.call(PRODUCT_STATUS_COLORS, status)
                ? PRODUCT_STATUS_COLORS[status as keyof typeof PRODUCT_STATUS_COLORS]
                : PRODUCT_STATUS_COLORS.DEFAULT;
            return (
                <StatusWrapper color={color}>
                    {t(`dictionaries.products.status.${status}`, {defaultValue: status})}
                </StatusWrapper>
            );
        }
        if (key === 'remarkingReason')
            return t(`dictionaries.documents.remarking.reasons.${value}`, {
                defaultValue: String(value),
            });
        if (key === 'sourceFormation')
            return t(`dictionaries.products.blockingSourceFormation.${value}`, {
                defaultValue: String(value),
            });
        if (key === 'oldCodeDisposalReason' && value === 'REMARKING')
            return t('pages.remarkingAudit.tab');
        return value;
    };
    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.REMARKING_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {pending ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            <EventBadge>
                                {t(`pages.remarkingAudit.events.${entry.eventType}`, {
                                    defaultValue: entry.eventType,
                                })}
                            </EventBadge>
                            {permissions.includes(PERMISSION_ALIASES.remarkingAuditLogExport) && (
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    disabled={exportPending}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={DownloadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            )}
                        </DetailHeaderActions>
                    </DetailHeader>
                    <AuditGeneralInformation entry={entry}>
                        {([
                            'organizationName',
                            'organizationId',
                            'organizationTin',
                            'productCode',
                            'sourceFormation',
                        ] as const).map((key) => (
                            <Field key={key}>
                                <FieldLabel>{t(`pages.remarkingAudit.fields.${key}`)}</FieldLabel>
                                <FieldValue>{renderValue(key)}</FieldValue>
                            </Field>
                        ))}
                    </AuditGeneralInformation>
                    <Section>
                        <SectionTitle>{t('pages.remarkingAudit.details')}</SectionTitle>
                        <FieldsGrid>
                            {(eventFields[entry.eventType] || [])
                                .filter((key) => key !== 'comment' || Boolean(entry.comment))
                                .map((key) => (
                                    <Field key={key} wide={key === 'comment'}>
                                        <FieldLabel>
                                            {t(`pages.remarkingAudit.fields.${key}`)}
                                        </FieldLabel>
                                        <FieldValue>{renderValue(key)}</FieldValue>
                                    </Field>
                                ))}
                        </FieldsGrid>
                    </Section>
                </DetailCard>
            )}
        </Page>
    );
};
